import { GrayRoundInput, GrayRoundTextArea, Text, Title } from "@smoothincorg/smooth-ui"
import Link from "next/link"
import React, { useState } from "react"
import { HiExternalLink } from "react-icons/hi"
import { Border } from "src/components/Border"
import { FlexBox } from "src/components/Box"
import { BorderedButton } from "src/components/Button"
import { Memo, MemoType } from "src/components/Memo"
import { Card } from "src/components/page-directory/Card"
import { useSmoothApi } from "src/hooks/useSmoothApi"
import { SMOOTH_SMOOTHER_API_URL, V1_OPE_URL } from "src/lib/constants/value"
import { dayjs } from "src/lib/dayjs"
import styled from "styled-components"
import { ForCICProps } from "./ForCIC"
import { CalcAge, FormatTelephoneNumber } from "./MainExaminationScore"
import { useNotification } from "@smoothincorg/smooth-ui"

type ContainerProps = ForCICProps & {
  user: ForCICProps["users"][number]
  mes: ForCICProps["mainExaminationScores"][number]
  // searchHouse: ForCICProps['searchHouses'][number]
  mutateForCIC: (targetId: number) => void
}

type Props = ContainerProps & {
  id: number
  handleDestroyMemo: (memo: MemoType) => void
  handleOpenMemoForm: (memo: MemoType) => void
  memoFormState: MemoFormState
  handleChangeMemoForm: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>
  handleSubmitMemo: () => Promise<void>
  lendableLoan: number | "all"
  claimPaymentAmountOnAnnual: number
  exception: boolean
  additionalAmountByException: number
}

// eslint-disable-next-line react/display-name
const Component = React.memo<Props>(({ user, mes, ...props }) => {
  const { patchWithToken } = useSmoothApi()
  const birthday = user?.birthday
  const { showSnackbar } = useNotification()

  const handleAssignSmootherClick = async (mes_id: number) => {
    const result: any = await patchWithToken(
      `${SMOOTH_SMOOTHER_API_URL}/main_examination_scores/` + mes_id + "/assign_smoother"
    )

    showSnackbar(`${result.data.assigned_smoother}に変更しました`, "success")
    props.mutateForCIC(mes_id)
  }

  return (
    <Card className={`main-examination-score ${props.className}`} data-id={props.id}>
      <FlexBox className={`main-examination-score__row`} alignItems="center">
        <Text size={16} weight="bold">
          審査担当者
        </Text>
        <Text size={12} style={{ marginLeft: 10 }}>
          {mes.assigned_smoother || "担当者なし"}
        </Text>
        <BorderedButton
          style={{ marginLeft: 10 }}
          onClick={() => {
            if (!confirm("担当者を自分に変更しますか？")) return

            handleAssignSmootherClick(mes.id)
          }}
        >
          審査担当者を自分に変更する
        </BorderedButton>
      </FlexBox>
      <Border />
      <FlexBox className={`main-examination-score__row`} alignItems="center">
        <Text size={12} style={{ marginLeft: 10 }}>
          {dayjs(mes.created_at).format("YYYY-MM-DD HH:mm")}
        </Text>
      </FlexBox>
      <Border />
      <FlexBox className={`main-examination-score__row`} alignItems="center">
        <Title as="h3" size={14} weight="bold">
          照会キー
        </Title>
        <FlexBox justifyContent="flex-start" alignItems="center">
          <a href={`${V1_OPE_URL}/admin/users/${user.id}`} target="_blank" rel="noreferrer">
            <Text size={14} color="main">
              {user.last_furigana}
              {` `}
              {user.first_furigana}
              <HiExternalLink />
            </Text>
          </a>
          <Link href={`/admin/users/${user.id}`}>
            <Text
              size={14}
              color="main"
              style={{
                textDecoration: "underline",
              }}
            >
              {user.id}
            </Text>
          </Link>

          <Text size={14}>
            {user.birthday_ja}({CalcAge(birthday)})
          </Text>
          <Text size={14}>{FormatTelephoneNumber(user.telephone_number)}</Text>
          <Text size={14}>{user.postal_code}</Text>
          <Text size={14}>{user.proposals_documents}</Text>
          <a href={`${user.around_initial_cost_url}`} target="_blank" rel="noreferrer">
            <Text size={14} color="main">
              {user.around_initial_cost}
              {user.around_initial_cost_status ? `(${user.around_initial_cost_status})` : null}
            </Text>
          </a>
        </FlexBox>
      </FlexBox>
      <Border />
      <FlexBox className={`main-examination-score__row`} alignItems="center">
        <Title as="h3" size={14} weight="bold">
          本人確認情報
        </Title>
        <FlexBox justifyContent="flex-start" alignItems="center">
          <span className="icon-src">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src={user.icon_src} alt="user_icon" />
          </span>
          <Text size={14}>{user.line_user_name}</Text>
        </FlexBox>
      </FlexBox>
      <Border />
      <FlexBox
        alignItems="center"
        justifyContent="flex-start"
        className={`main-examination-score__row`}
      >
        <Title as="h3" size={14} weight="bold">
          メモ
        </Title>
        <FlexBox flexDirection="column" className="memos">
          {user.memos.map((memo) => (
            <Memo
              key={memo.id}
              memo={memo}
              destroyMemo={props.handleDestroyMemo}
              openMemoForm={props.handleOpenMemoForm}
            />
          ))}
        </FlexBox>
        <FlexBox flexDirection="column" className="memo-form">
          <section className="">
            <GrayRoundInput
              type="text"
              onChange={props.handleChangeMemoForm}
              name={"title"}
              value={props.memoFormState.title}
              placeholder="メモのタイトルを入力して下さい"
            />
          </section>
          <section className="">
            <GrayRoundTextArea
              onChange={props.handleChangeMemoForm}
              name={"body"}
              value={props.memoFormState.body}
              rows={12}
              placeholder="メモの本文を入力して下さい"
            />
          </section>
          <div className={`${``}__actions`}>
            <BorderedButton>キャンセル</BorderedButton>
            <BorderedButton onClick={props.handleSubmitMemo}>保存</BorderedButton>
          </div>
        </FlexBox>
      </FlexBox>
    </Card>
  )
})

const StyledComponent = styled(Component)`
  .pass-actions {
    padding: 20px 0;
    width: 80%;
    margin: 10px auto;
    > .smooth-ui-FlexBox {
      width: 90%;
      margin: 14px auto;
      > .smooth-ui-FlexBox,
      > .radios {
        width: 300px;
      }

      .checkbox {
        height: 44px;
        margin-left: 8px;
      }

      > .smooth-ui-Title {
        width: 200px;
      }
    }
  }

  .icon-src {
    width: 50px;
    height: 50px;
    overflow: hidden;
    border-radius: 25px;
  }

  .adjust {
    .smooth-ui-GrayRoundInput {
      width: 120px;
    }
  }
`
type MemoFormState = {
  title: string
  body: string
}

type State = {
  memoForm: {
    title: string
    body: string
  }
  lendableLoan: number | "all"
  // 千円単位
  claimPaymentAmountOnAnnual: number
  additionalAmountByException: number
  openedMemo: number | null
  exception: boolean
}

const Container: React.VFC<ContainerProps> = (props) => {
  const { deleteWithToken, patchWithToken, postWithToken } = useSmoothApi()

  const [state, setState] = useState<State>({
    memoForm: {
      title: "",
      body: "",
    },
    lendableLoan: null,
    claimPaymentAmountOnAnnual: null,
    additionalAmountByException: null,
    openedMemo: null,
    exception: false,
  })

  const handleDestroyMemo = async (memo: MemoType) => {
    try {
      await deleteWithToken(SMOOTH_SMOOTHER_API_URL + "/deserialized_memos/" + memo.id)
    } catch (e) {
      alert("失敗した。もう一回お願いします。")
      return
    }

    setState((current) => ({
      ...current,
      memoForm: { title: "", body: "" },
    }))
  }

  const handleOpenMemoForm = (memo: MemoType) => {
    setState((current) => ({
      ...current,
      memoForm: memo,
      openedMemo: memo.id,
    }))
  }

  const handleChangeMemoForm: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement> = (
    e
  ) => {
    setState((current) => ({
      ...current,
      memoForm: { ...current.memoForm, [e.target.name]: e.target.value },
    }))
  }

  const handleSubmitMemo = async () => {
    const payload = {
      memo: {
        memoable_id: props.currentSmoother.id,
        user_id: props.user.id,
        ...state.memoForm,
      },
    }

    try {
      state.openedMemo
        ? await patchWithToken(
            SMOOTH_SMOOTHER_API_URL + "/deserialized_memos/" + state.openedMemo,
            payload
          )
        : await postWithToken(SMOOTH_SMOOTHER_API_URL + "/deserialized_memos/", payload)
    } catch (e) {
      alert("失敗した。もう一回お願いします。")
      return
    }

    alert("メモ作成完了")
  }

  return (
    <StyledComponent
      {...props}
      id={props.mes.id}
      handleDestroyMemo={handleDestroyMemo}
      handleOpenMemoForm={handleOpenMemoForm}
      memoFormState={state.memoForm}
      handleChangeMemoForm={handleChangeMemoForm}
      handleSubmitMemo={handleSubmitMemo}
      lendableLoan={state.lendableLoan}
      claimPaymentAmountOnAnnual={state.claimPaymentAmountOnAnnual}
      exception={state.exception}
      additionalAmountByException={state.additionalAmountByException}
    />
  )
}

export const MainExaminationScoreSimple = Container
